/* MUTO Creatine page — the benefits toggle and the gallery.
 *
 * Deliberately small. The accordions are <details>, so they open with no
 * script and are findable by in-page search; the plan selector and the sticky
 * bar are the home page's, because the buy block drives the same bar and two
 * implementations of one selection would be two things to keep in step.
 *
 * Idempotent; re-binds on Shopify theme editor section loads.
 */
(function () {
  'use strict';

  /* Tabs. The markup ships every panel visible and in document order, so with
   * no JS the band is long but complete rather than silently missing half its
   * content. The script is what turns it into one-at-a-time: it hides the
   * others, marks the group as tabbed, and wires the roving arrow-key
   * behaviour a tablist is expected to have. */
  function initTabs(root) {
    var groups = root.querySelectorAll('[data-mc-tabs]');
    for (var i = 0; i < groups.length; i++) {
      (function (group) {
        if (group.dataset.mcReady === 'true') return;
        var tabs = [].slice.call(group.querySelectorAll('[data-mc-tab]'));
        if (tabs.length < 2) return;
        group.dataset.mcReady = 'true';
        group.classList.add('mc--tabbed');

        function select(tab, focus) {
          for (var j = 0; j < tabs.length; j++) {
            var on = tabs[j] === tab;
            tabs[j].setAttribute('aria-selected', on ? 'true' : 'false');
            /* Only the selected tab is a tab stop; the arrows move between
             * them, which is how a tablist is meant to behave. */
            tabs[j].tabIndex = on ? 0 : -1;
            var id = tabs[j].getAttribute('aria-controls');
            if (!id) continue;
            var panel = group.querySelector('#' + CSS.escape(id));
            if (panel) panel.hidden = !on;
            /* A tab here owns more than its panel: the benefits band's heading
             * and lede sit above the pill and change with it. Anything naming
             * the panel follows the panel. */
            var extra = group.querySelectorAll('[data-mc-for="' + id + '"]');
            for (var m = 0; m < extra.length; m++) extra[m].hidden = !on;
          }
          if (focus) tab.focus();
        }

        for (var k = 0; k < tabs.length; k++) {
          (function (tab, idx) {
            tab.addEventListener('click', function () { select(tab, false); });
            tab.addEventListener('keydown', function (e) {
              var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 :
                      e.key === 'Home' ? -idx : e.key === 'End' ? tabs.length - 1 - idx : 0;
              if (!d) return;
              e.preventDefault();
              select(tabs[(idx + d + tabs.length) % tabs.length], true);
            });
          })(tabs[k], k);
        }

        var start = group.querySelector('[data-mc-tab][aria-selected="true"]') || tabs[0];
        select(start, false);
      })(groups[i]);
    }
  }

  /* Gallery. The thumbnail rail ships hidden and is revealed here, so with no
   * script the band shows the lead photograph and no dead controls — rather
   * than five buttons that look clickable and aren't. */
  function initGallery(root) {
    var galleries = root.querySelectorAll('[data-mc-gallery]');
    for (var i = 0; i < galleries.length; i++) {
      (function (gal) {
        if (gal.dataset.mcReady === 'true') return;
        var thumbs = [].slice.call(gal.querySelectorAll('[data-mc-thumb]'));
        var slides = [].slice.call(gal.querySelectorAll('[data-mc-slide]'));
        if (thumbs.length < 2 || slides.length < 2) return;
        gal.dataset.mcReady = 'true';

        var rail = gal.querySelector('[data-mc-rail]');
        if (rail) rail.hidden = false;

        function show(n) {
          for (var j = 0; j < slides.length; j++) slides[j].hidden = j !== n;
          for (var k = 0; k < thumbs.length; k++) {
            thumbs[k].setAttribute('aria-current', k === n ? 'true' : 'false');
          }
        }

        for (var t = 0; t < thumbs.length; t++) {
          (function (btn, idx) {
            btn.addEventListener('click', function () { show(idx); });
          })(thumbs[t], t);
        }
        show(0);
      })(galleries[i]);
    }
  }

  /* Ingredient filter. The control ships hidden and is revealed here, so with
   * no script the band shows all nineteen ingredients rather than four buttons
   * that look clickable and aren't. Filtering sets `hidden` on the tile rather
   * than a class: a hidden tile is out of the accessibility tree and out of
   * in-page search, which is what "filtered out" should mean. */
  function initFilter(root) {
    var bands = root.querySelectorAll('[data-mc-ing]');
    for (var i = 0; i < bands.length; i++) {
      (function (band) {
        if (band.dataset.mcReady === 'true') return;
        var group = band.querySelector('[data-mc-ing-filter]');
        var items = [].slice.call(band.querySelectorAll('[data-mc-ing-item]'));
        if (!group || !items.length) return;
        var buttons = [].slice.call(group.querySelectorAll('[data-mc-ing-group]'));
        if (buttons.length < 2) return;
        band.dataset.mcReady = 'true';
        group.hidden = false;

        function apply(want) {
          for (var j = 0; j < items.length; j++) {
            items[j].hidden = want !== '' && items[j].getAttribute('data-mc-ing-item') !== want;
          }
          for (var k = 0; k < buttons.length; k++) {
            var on = buttons[k].getAttribute('data-mc-ing-group') === want;
            buttons[k].setAttribute('aria-pressed', on ? 'true' : 'false');
          }
          measureIngList(band);
        }

        for (var b = 0; b < buttons.length; b++) {
          (function (btn) {
            btn.addEventListener('click', function () {
              apply(btn.getAttribute('data-mc-ing-group'));
            });
          })(buttons[b]);
        }
        apply('');
      })(bands[i]);
    }
  }

  /* The ingredient list's cap and its position indicator.
   *
   * At phone width the list shows three tiles and scrolls. The stylesheet has
   * a fallback height for a scriptless browser, but a tile is only as tall as
   * the name inside it, so the cap is measured here from the real tiles: the
   * top of the fourth visible one, less the list's own top, plus a sliver so
   * it is clearly cut rather than neatly ended. Fewer than four tiles — a
   * short formula, or a filter that has narrowed the list — means nothing to
   * scroll, so the cap and the indicator both come off.
   *
   * Re-run on resize and after every filter press, because both change which
   * tile is fourth and how tall it is. */
  function measureIngList(band) {
    var list = band.querySelector('.mc__ing-list');
    var bar = band.querySelector('[data-mc-ing-scroll]');
    if (!list) return;
    var thumb = bar && bar.querySelector('[data-mc-ing-thumb]');

    function off() {
      list.style.removeProperty('--mc-ing-max');
      if (bar) bar.removeAttribute('data-mc-on');
    }

    /* The desktop grid is not a scroller. Reading the computed columns rather
     * than matching a width keeps the breakpoint in the stylesheet, where the
     * rest of it lives. */
    var cols = getComputedStyle(list).gridTemplateColumns.split(' ').length;
    if (cols > 1) { off(); return; }

    var shown = [];
    for (var i = 0; i < list.children.length && shown.length < 4; i++) {
      if (!list.children[i].hidden) shown.push(list.children[i]);
    }
    if (shown.length < 4) { off(); return; }

    /* offsetTop is relative to the nearest positioned ancestor, so the two are
     * differenced rather than either being trusted on its own. */
    /* +18, not a few pixels: the tile's corner radius is 16, so a shallower
     * cut shows only curve and reads as a rendering seam rather than a card. */
    var cap = shown[3].offsetTop - shown[0].offsetTop + 18;
    list.style.setProperty('--mc-ing-max', cap + 'px');
    if (!bar || !thumb) return;
    bar.setAttribute('data-mc-on', '');

    function paint() {
      var room = list.scrollHeight - list.clientHeight;
      var frac = list.clientHeight / list.scrollHeight;
      thumb.style.width = (frac * 100).toFixed(2) + '%';
      var travel = room > 0 ? list.scrollTop / room : 0;
      thumb.style.transform = 'translateX(' + (travel * (100 / frac - 100)).toFixed(2) + '%)';
    }
    if (!list.dataset.mcScrollBound) {
      list.dataset.mcScrollBound = 'true';
      list.addEventListener('scroll', paint, { passive: true });
      /* `toggle` does not bubble, so it is caught on the way down. Opening a
       * tile changes how much there is to scroll, which changes the thumb. */
      list.addEventListener('toggle', paint, true);
    }
    paint();
  }

  function initIngScroll(root) {
    var bands = root.querySelectorAll('[data-mc-ing]');
    for (var i = 0; i < bands.length; i++) measureIngList(bands[i]);
  }

  /* Disclosures. Every expander on these pages is a <details>, which needs no
   * script at all — the browser toggles it. But this page now runs inside a
   * live theme beside its own JavaScript and whatever app embeds the store has
   * installed, and anything that swallows a click before the browser acts on it
   * leaves an expander that looks broken: you click it and nothing happens.
   *
   * So this watches for the click at the document, in the capture phase, before
   * an ancestor handler can stop it travelling — then checks, after the browser
   * has had its turn, whether the toggle actually happened. If it did, this does
   * nothing, which is the case everywhere it already works. If it didn't, this
   * does it. One listener, and it covers expanders added later by the theme
   * editor as well.
   */
  function rescue(e) {
    /* One click, one rescue: this is reached from two places on purpose and
     * must not toggle twice. */
    if (!e || e.mcSeen) return;
    var t = e.target;
    if (!t || !t.closest) return;
    var sum = t.closest('summary');
    if (!sum || !sum.closest('.mc')) return;
    var box = sum.parentNode;
    if (!box || box.tagName !== 'DETAILS') return;
    e.mcSeen = true;
    var was = box.open;
    setTimeout(function () { if (box.open === was) box.open = !was; }, 0);
  }

  /* The welcome kit's expander is deliberately NOT here. It is a button and a
   * panel with its handler inline in muto-crea-buy.liquid, so the control and
   * the code that works it arrive in the same response — see the comment there.
   * Nothing in this file should grow a second copy of it. */

  function initDisclosures() {
    if (document.documentElement.dataset.mcDisclosures === 'true') return;
    document.documentElement.dataset.mcDisclosures = 'true';
    /* window first, then document: a listener on window in the capture phase
     * runs before anything bound to the document can stop the event
     * travelling. Both are hung here, and the dedupe above means two hits on
     * the same click still toggle once. */
    window.addEventListener('click', rescue, true);
    document.addEventListener('click', rescue, true);
  }

  /* The comparison table's highlight is one panel behind MUTO's column rather
   * than a background on seven separate cells, so it can have one rounded
   * outline. Its left edge and width are whatever that column measures, which
   * is only known once the table has laid out. */
  function placeHighlight(root) {
    var tables = root.querySelectorAll('[data-mc-compare]');
    for (var i = 0; i < tables.length; i++) {
      (function (wrap) {
        function place() {
          var tbody = wrap.querySelector('tbody');
          var panel = wrap.querySelector('[data-mc-panel]');
          var cell = wrap.querySelector('[data-mc-muto-col]');
          if (!tbody || !panel || !cell || wrap.offsetParent === null) return;
          var wb = wrap.getBoundingClientRect();
          var bb = tbody.getBoundingClientRect();
          var cb = cell.getBoundingClientRect();
          /* The artboard's panel starts 7.5 above the first row and finishes
           * 7.5 below the last, and is a fixed 169 wide centred on the column
           * rather than the column's own width. */
          var w = parseFloat(getComputedStyle(panel).width) || 169;
          var mid = cb.left + cb.width / 2 - wb.left;
          wrap.style.setProperty('--mc-cmp-left', (mid - w / 2) + 'px');
          wrap.style.setProperty('--mc-cmp-top', (bb.top - wb.top - 7.5) + 'px');
          wrap.style.setProperty('--mc-cmp-h', (bb.height + 15) + 'px');
        }
        place();
        if (wrap.dataset.mcReady === 'true') return;
        wrap.dataset.mcReady = 'true';
        var t;
        window.addEventListener('resize', function () {
          clearTimeout(t);
          t = setTimeout(place, 120);
        }, { passive: true });
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(place);
      })(tables[i]);
    }
  }

  /* Each part of the page is wired independently, so one of them throwing on a
   * DOM this script did not expect cannot take the rest of the page with it.
   * Before this, a failure in the first line left every later one unbound —
   * including the disclosure rescue, which is the one that has to survive. */
  /* Quantity: the visible field belongs to the subscription form, because a
   * field belongs to one form only. The one-time line is a second form with a
   * hidden quantity of its own, and this keeps it in step. With no script the
   * picker still posts the right number on the main button; only the one-time
   * line falls back to one. */
  function initQuantity(root) {
    var picker = root.querySelector('[data-mc-qty]');
    if (!picker || picker.dataset.mcQtyBound === 'true') return;
    picker.dataset.mcQtyBound = 'true';
    function clamp() {
      var n = parseInt(picker.value, 10);
      return n > 0 ? n : 1;
    }
    function mirror() {
      var n = clamp();
      var fields = document.querySelectorAll('[data-mc-qty-mirror]');
      for (var i = 0; i < fields.length; i++) fields[i].value = n;
    }
    picker.addEventListener('input', mirror);
    /* On commit — blur, or the spinner — the field itself is put right. The
     * subscription form posts this input directly rather than a mirror of it,
     * so an emptied or zeroed box would otherwise send a blank quantity. Not
     * done on `input`: that would fight someone clearing the box to retype. */
    picker.addEventListener('change', function () {
      var n = clamp();
      if (String(n) !== picker.value) {
        picker.value = n;
        /* So the price on the button follows the correction. */
        picker.dispatchEvent(new Event('input', { bubbles: true }));
      }
      mirror();
    });
    mirror();
  }

  function safe(fn, arg) {
    try { fn(arg); } catch (err) {
      if (window.console && console.warn) console.warn('muto-crea: ' + fn.name, err);
    }
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    safe(initTabs, scope);
    safe(initGallery, scope);
    safe(initFilter, scope);
    safe(initIngScroll, scope);
    safe(placeHighlight, scope);
    safe(initQuantity, scope);
  }

  /* The cap is three real tiles, so it has to be remeasured when the width
   * that decides how those tiles lay out changes — including the crossing of
   * the breakpoint, where the list stops being a scroller at all. */
  var ingResize;
  window.addEventListener('resize', function () {
    clearTimeout(ingResize);
    ingResize = setTimeout(function () { safe(initIngScroll, document); }, 150);
  });

  /* Bound now, not on DOMContentLoaded and not after the inits above: the
   * rescue is the safety net, so nothing else gets a chance to stop it being
   * installed. Listening on window needs no DOM. */
  safe(initDisclosures);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
